// import node module libraries
import { Fragment, useEffect, useState } from 'react';
import { Col, Row, Breadcrumb, Card, Form, Button, Spinner } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

// Import authentication components
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import useAuthUser from 'react-auth-kit/hooks/useAuthUser';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const AdminCronJobs = ({ activeKey }) => { // activeKey als Prop erhalten
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isLoading, setIsLoading] = useState(true); // State for loading table
	const [isLoadingButton, setIsLoadingButton] = useState(false); // State for loading spinner
	const [isButtonDisabled, setIsButtonDisabled] = useState(false); // State for button disabled when loading

	const auth = useAuthUser(); // Assuming this gets the auth user data
	const [formData, setFormData] = useState(null);
	const [data, setData] = useState(null); // Add data state

	// API-Call Options
	const authHeader = useAuthHeader();
	const options = {
		params: {

		},
		headers: {
			Authorization: authHeader,
			Accept: 'application/json'
		}
	};

	// API-Call for all Data
	const getData = async () => {
		setIsLoading(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.get(`${baseURL}/configs/b24e9a8c-0fae-11ef-866a-0242ac120002`, options)
			.then(response => {
				const responseData = response.data.data; // Extract the data from the response
				setData({ data: responseData });
				setFormData({
					config_key: responseData.config_key || '',
					config_value: {
						cron_jobs: {
							returns: responseData.config_value.cron_jobs.returns.map(job => ({
								id: job.id,
								job_name: job.job_name,
								cron_expression: job.cron_expression
							}))
						}
					}
				});
				setIsLoading(false);
			})
			.catch((error) => {
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
				setIsLoading(false);
			});
	};

	useEffect(() => {
		// Check if the current tab is active before making the API call
		if (activeKey === 'cronjobs') {
			getData();
		}
	}, [activeKey]); // Run effect when activeKey changes

	const handleChange = (e) => {
		const { id, name, value } = e.target;
		
		const newJob = {
			id,
			job_name: name,
			cron_expression: value,
		};
		
		// Update the form data
		setFormData((prevFormData) => {
			const updatedCrons = prevFormData.config_value.cron_jobs.returns.map((job) =>
				job.id === id ? newJob : job // Update the job if the ID matches, otherwise keep the original job
			);

			return {
				...prevFormData,
				config_value: {
					...prevFormData.config_value,
					cron_jobs: {
						returns: updatedCrons, // Keep it as an array
					},
				},
			};
		});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setIsLoadingButton(true);

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.patch(`${baseURL}/configs/b24e9a8c-0fae-11ef-866a-0242ac120002`, formData, options)
			.then(response => {
				setData({ data: response.data.data });
				setIsLoadingButton(false);
				handleShowNotification(setNotification, 'success', 'Erfolgreich', 'Deine Daten wurden erfolgreich gespeichert');
			})
			.catch((error) => {
				setIsLoadingButton(false);
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Bitte versuche es erneut oder kontaktiere den HelpDesk.');
			});
	};

	return (
		<Fragment>
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			<Row>
				<Col xl={{ offset: 1, span: 10 }} lg={{ offset: 1, span: 10 }} md={12}>
					<Card className="mb-4">
						<Card.Header className="d-flex align-items-center justify-content-between">
							<h4 className="mb-0">{t('Returns')}</h4>
						</Card.Header>
						<Card.Body>
							{isLoading ? (
								<div className="d-flex justify-content-center">
									<Spinner animation="border" role="status">
										<span className="visually-hidden">Loading...</span>
									</Spinner>
								</div>
							) : (
								<Form onSubmit={handleSubmit}>
									<Row>
										<Col lg={12} md={12} className="mb-3 d-none">
											<Form.Group controlId="id">
												<Form.Label visuallyHidden >ID</Form.Label>
												<Form.Control 
													type="text" 
													value={formData?.id} 
													onChange={handleChange} 
													disabled 
												/>
											</Form.Group>
										</Col>
										<Col lg={12} md={12} className="mb-3">
											{/* getReturnsFromAirbyte */}
											<Form.Group controlId={formData?.config_value?.cron_jobs?.returns[0]?.id}>
												<Form.Label>
													{formData?.config_value?.cron_jobs?.returns[0]?.job_name}{' '}
													<small className="text-muted">
														<Link target='_blank' to={`/administration/help-center/general-settings/cron-jobs/return/#${formData?.config_value?.cron_jobs?.returns[0]?.job_name}`}>
															{t('Help')}
														</Link>
													</small>
												</Form.Label>
												<Form.Control
													type="text"
													name={formData?.config_value?.cron_jobs?.returns[0]?.job_name}
													value={formData?.config_value?.cron_jobs?.returns[0]?.cron_expression}
													onChange={handleChange}
												/>
											</Form.Group>
										</Col>

										<Col lg={12} md={12} className="mb-3">
											{/* createTenantQueueRequest */}
											<Form.Group controlId={formData?.config_value?.cron_jobs?.returns[1]?.id}>
												<Form.Label>
													{formData?.config_value?.cron_jobs?.returns[1]?.job_name}{' '}
													<small className="text-muted">
														<Link target='_blank' to={`/administration/help-center/general-settings/cron-jobs/return/#${formData?.config_value?.cron_jobs?.returns[1]?.job_name}`}>
															{t('Help')}
														</Link>
													</small>
												</Form.Label>
												<Form.Control
													type="text"
													name={formData?.config_value?.cron_jobs?.returns[1]?.job_name}
													value={formData?.config_value?.cron_jobs?.returns[1]?.cron_expression}
													onChange={handleChange}
												/>
											</Form.Group>
										</Col>
										<Col lg={12} md={12} className="mb-3">
											{/* updateTenantReturnWithQueueResponse */}
											<Form.Group controlId={formData?.config_value?.cron_jobs?.returns[2]?.id}>
												<Form.Label>
													{formData?.config_value?.cron_jobs?.returns[2]?.job_name}{' '}
													<small className="text-muted">
														<Link target='_blank' to={`/administration/help-center/general-settings/cron-jobs/return/#${formData?.config_value?.cron_jobs?.returns[2]?.job_name}`}>
															{t('Help')}
														</Link>
													</small>
												</Form.Label>
												<Form.Control
													type="text"
													name={formData?.config_value?.cron_jobs?.returns[2]?.job_name}
													value={formData?.config_value?.cron_jobs?.returns[2]?.cron_expression}
													onChange={handleChange}
												/>
											</Form.Group>
										</Col>
										<Col lg={12} md={12} className="mb-3">
											{/* createTenantReturnFile */}
											<Form.Group controlId={formData?.config_value?.cron_jobs?.returns[3]?.id}>
												<Form.Label>
													{formData?.config_value?.cron_jobs?.returns[3]?.job_name}{' '}
													<small className="text-muted">
														<Link target='_blank' to={`/administration/help-center/general-settings/cron-jobs/return/#${formData?.config_value?.cron_jobs?.returns[3]?.job_name}`}>
															{t('Help')}
														</Link>
													</small>
												</Form.Label>
												<Form.Control
													type="text"
													name={formData?.config_value?.cron_jobs?.returns[3]?.job_name}
													value={formData?.config_value?.cron_jobs?.returns[3]?.cron_expression}
													onChange={handleChange}
												/>
											</Form.Group>
										</Col>
									</Row>
									<Row>
										<Col lg={12} md={12}>
											<div className="d-flex justify-content-end mt-4">
												<Button variant="primary" type="submit" disabled={isLoadingButton}>
													{isLoadingButton ? (
														<>
															<Spinner animation="border" size="sm" className="me-2" />
															{t('Save')}
														</>
													) : (
														t('Save')
													)}
												</Button>
											</div>
										</Col>
									</Row>
								</Form>
							)}	
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</Fragment>
	);
};

export default AdminCronJobs;
